import { useIsFormerMember } from '@fluxter/shared';
import { cn } from '@/lib/utils';

export interface UserNameProps {
  /** Workspace context — required so the former-member check can
   *  reach the right member list. Pass `undefined` to skip the
   *  former-state lookup entirely (rendering as plain text). */
  workspaceSlug: string | undefined;
  userId: string | null | undefined;
  name?: string | null;
  email?: string | null;
  /** Override fallback when neither name nor email is present. */
  fallback?: string;
  className?: string;
}

/**
 * Renders a user's display name with a subtle `(former)` suffix
 * when the user is no longer a workspace member. Powers the audit
 * surfaces (comment threads, activity feed, time entries, note
 * attribution) so orphaned authors stay attributed but visually
 * de-emphasized.
 *
 * The badge silently no-ops while the members query is loading
 * to avoid a flash of false-positive "(former)" tags on first
 * paint.
 */
export const UserName = ({
  workspaceSlug,
  userId,
  name,
  email,
  fallback = 'Unknown',
  className,
}: UserNameProps) => {
  const { isFormer } = useIsFormerMember(workspaceSlug, userId);
  const label = name ?? email ?? fallback;

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span className={cn(isFormer && 'text-muted-foreground')}>{label}</span>
      {isFormer ? (
        <span
          className="text-muted-foreground/70 text-[10px] font-normal uppercase tracking-wide"
          title="No longer a workspace member"
        >
          (former)
        </span>
      ) : null}
    </span>
  );
};
